// utils/youtube.ts

// Robust ID extractor
export function extractYouTubeId(url: string): string | null {
    try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, '');

        // youtu.be/<id>
        if (host === 'youtu.be') {
            const id = u.pathname.split('/')[1];
            return id || null;
        }

        // *.youtube.com
        if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
            // Most common: /watch?v=<id>
            const v = u.searchParams.get('v');
            if (v) return v;

            // Other common paths: /embed/<id>, /live/<id>, /shorts/<id>, /v/<id>, /e/<id>
            const parts = u.pathname.split('/').filter(Boolean);
            const first = parts[0]?.toLowerCase();
            if (['embed', 'live', 'shorts', 'v', 'e'].includes(first) && parts[1]) {
                return parts[1];
            }

            // Some share links nest a watch URL in ?u= or ?q=
            const nested = u.searchParams.get('u') || u.searchParams.get('q');
            if (nested) {
                try {
                    return extractYouTubeId(decodeURIComponent(nested));
                } catch {
                    /* ignore */
                }
            }
        }

        return null;
    } catch {
        return null;
    }
}

// Thumbnail builder (uses the extractor)
export const getYouTubeThumbnail = (url: string): string | null => {
    const videoId = extractYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/0.jpg` : null;
};

export function resolveSetlistSongMedia({
    songArt,
    avatar,
    fileUrl,
    isPublicDomain = false,
    publicDomainFallbackUrl = '',
}: {
    songArt?: string | null;
    avatar?: string | null;
    fileUrl?: string | null;
    isPublicDomain?: boolean;
    publicDomainFallbackUrl?: string;
}) {
    const normalizedFileUrl = typeof fileUrl === 'string' && fileUrl.trim()
        ? fileUrl.trim()
        : (isPublicDomain ? publicDomainFallbackUrl : '');
    const resolvedAvatar = songArt
        || avatar
        || (normalizedFileUrl ? getYouTubeThumbnail(normalizedFileUrl) : null)
        || (isPublicDomain ? publicDomainFallbackUrl : null);

    return {
        fileUrl: normalizedFileUrl,
        avatar: resolvedAvatar,
    };
}

export async function fetchOEmbedMetadata(url: string) {
    try {
        const endpoint = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error('Invalid oEmbed response');
        return await response.json();
    } catch (err) {
        console.error("oEmbed fetch failed:", err);
        return null;
    }
}
